import { memo, useRef, useState } from "react";
import { imageRotationClass } from "utils/cameraImage";
import {
  CartesianGrid,
  Customized,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  usePlotArea,
} from "recharts";

// For a merged multi-source series, the hovered row only ever has ONE
// source's fields defined (they were never sampled at the same instant) -
// Recharts' default tooltip payload reflects only that one row, so hovering
// shows at most one line's value at a time, and which one flips as the
// cursor moves a few pixels (reported as values appearing "staggered").
// This looks up each line's nearest ACTUAL sample independently and labels
// it with its own real timestamp - never fabricates a value, just stops
// hiding the other lines' nearest real readings.
// Recharts' default tick generation for a numeric `scale="time"` axis was
// producing one tick per distinct data timestamp instead of a handful of
// "nice" ones - with a couple thousand merged points that's an illegible
// comb of overlapping rotated labels, and duplicate/near-duplicate
// coordinates trip Recharts' own internal React key collision warning
// ("Encountered two children with the same key") hundreds of times per
// render. Passing an explicit, evenly-spaced `ticks` array sidesteps
// Recharts' own generator entirely, so the axis always renders exactly
// this many labeled ticks regardless of how many points are plotted.
export const AXIS_TICK_COUNT = 6;

// The XAxis below is `type="number"`/`scale="time"` over epoch-ms values -
// mathematically a plain linear scale in this space (a time scale doesn't
// bend the numeric axis, it only affects tick formatting), so re-deriving
// its domain here to place image markers stays exactly in sync with what
// the axis itself actually renders.
function getTimeDomain(data) {
  const values = data.map((d) => d.timeNumeric).filter((v) => typeof v === "number" && Number.isFinite(v));
  if (values.length === 0) return null;
  return [Math.min(...values), Math.max(...values)];
}

export function computeXTicks(data, tickCount = AXIS_TICK_COUNT) {
  const domain = getTimeDomain(data);
  if (!domain) return [];
  const [min, max] = domain;
  if (min === max) return [min];
  const step = (max - min) / (tickCount - 1);
  return Array.from({ length: tickCount }, (_, i) => Math.round(min + i * step));
}

// A line opting into `dot: true` (see Experiment.jsx's TEMP_LINES/
// WATER_POWER_LINES comment) was meant for a sparse series - dots make an
// otherwise-invisible handful of points visible against a dense chamber
// line. Real bug found 2026-09-26: PadiHAT's water-level cadence used to
// be a reliable ~15 minutes, but this project's own configurable
// lab-sampling feature (padihat-firmware-v0.3, 2026-09-25) can now make it
// report every few seconds during an experiment - the exact same field
// (d1_cal) that dot:true was written for can now have thousands of points
// in one run, and thousands of overlapping dot circles turned into visual
// clutter that made it harder to hover the exact instant wanted, not
// easier - precisely the problem dot:true was supposed to solve for the
// chamber's own dense series in the first place. Rather than removing
// dot:true (which would reintroduce the original invisible-sparse-line
// problem for a short run or the field firmware's still-slow ~5 min
// cadence), this makes it density-aware: only actually draw dots when
// this specific render's data for this field is still sparse enough for
// them to help.
export const DOT_DENSITY_THRESHOLD = 200;

export function countDefinedPoints(data, dataKey) {
  let count = 0;
  for (const row of data) {
    if (row[dataKey] !== undefined && row[dataKey] !== null) count++;
  }
  return count;
}

export function shouldShowDots(data, dataKey, wantsDot, threshold = DOT_DENSITY_THRESHOLD) {
  if (!wantsDot) return false;
  return countDefinedPoints(data, dataKey) <= threshold;
}

function nearestDefined(data, dataKey, targetTime) {
  let best = null;
  let bestDelta = Infinity;
  for (const row of data) {
    const value = row[dataKey];
    if (value === undefined || value === null) continue;
    const delta = Math.abs(row.timeNumeric - targetTime);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = row;
    }
  }
  return best ? { value: best[dataKey], timeNumeric: best.timeNumeric } : null;
}

const formatTooltipTime = (timestamp) =>
  new Date(timestamp).toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

function NearestValueTooltip({ active, label, data, lines }) {
  if (!active || label == null) return null;
  const entries = lines
    .map((line) => ({ line, match: nearestDefined(data, line.dataKey, label) }))
    .filter(({ match }) => match);
  if (entries.length === 0) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-2.5 text-xs">
      <div className="text-gray-500 mb-1.5">{formatTooltipTime(label)}</div>
      {entries.map(({ line, match }) => (
        <div key={line.dataKey} className="flex items-center gap-1.5 py-0.5">
          <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: line.color || "#63cdf1ff" }} />
          <span className="text-gray-700">{line.name || line.dataKey}:</span>
          <span className="font-semibold text-gray-900">
            {typeof match.value === "number" ? match.value.toFixed(2) : match.value}
          </span>
          {/* Only worth calling out when this line's nearest real sample
              wasn't actually at the hovered instant - i.e. it came from
              the other source's row. */}
          {Math.abs(match.timeNumeric - label) > 1000 && (
            <span className="text-gray-400">(at {formatTooltipTime(match.timeNumeric)})</span>
          )}
        </div>
      ))}
    </div>
  );
}

// Draws one small circle per ground-truth camera image at its real capture
// time. Rendered via recharts' <Customized> so it sits inside the chart's
// own React context, which is what makes the public usePlotArea() hook
// below resolve to this specific chart's plot-area geometry (recharts v3
// doesn't inject xAxisMap/offset as component props the way v2 did -
// usePlotArea/useOffset are the supported replacements, but there's no
// equivalently public x-scale hook, hence deriving the scale from
// historicalData's own domain instead of a Recharts-internal one). Markers
// sit just above the plot area so they never collide with a line's own
// points.
// A labelled marker reaches 4 + 6.5 px above the plot area; Recharts' default 5 px top margin cut the top half of every
// marker off (the SVG clips what is drawn outside it). Only charts that draw markers get the extra room.
export const MARKER_TOP_MARGIN = 14;

export function ImageMarkerLayer({ markers, domain, onHover, onClick }) {
  const plotArea = usePlotArea();
  if (!plotArea || !domain || markers.length === 0) return null;
  const [min, max] = domain;
  if (min === max) return null;
  const markerY = plotArea.y - 4;
  const scaleX = (value) => plotArea.x + ((value - min) / (max - min)) * plotArea.width;
  return (
    <g>
      {markers.map((marker) => {
        const x = scaleX(marker.x);
        if (!Number.isFinite(x) || x < plotArea.x - 0.5 || x > plotArea.x + plotArea.width + 0.5) return null;
        // A labelled photo (marker.labelled) is a bigger green dot with a tick, so the unlabelled
        // (small blue) ones stand out when going through them. A photo whose ruler reading waits for a
        // person to review it (marker.review) is an amber dot with "!".
        const kind = marker.labelled ? "labelled" : marker.review ? "review" : "plain";
        return (
          <g
            key={marker.id}
            data-testid={kind === "plain" ? "image-marker" : `image-marker-${kind}`}
            style={{ cursor: "pointer" }}
            onMouseEnter={(event) => onHover(marker, event)}
            onMouseLeave={() => onHover(null)}
            onClick={() => onClick?.(marker)}
          >
            <circle
              cx={x}
              cy={markerY}
              r={kind === "plain" ? 4 : 6.5}
              fill={kind === "labelled" ? "#16a34a" : kind === "review" ? "#d97706" : "#2563eb"}
              stroke="#fff"
              strokeWidth={1}
            />
            {kind === "labelled" && (
              <path
                d={`M ${x - 3} ${markerY} L ${x - 0.8} ${markerY + 2.4} L ${x + 3.2} ${markerY - 2.4}`}
                fill="none"
                stroke="#fff"
                strokeWidth={1.6}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
            {kind === "review" && (
              <>
                <line x1={x} y1={markerY - 3.4} x2={x} y2={markerY + 0.8} stroke="#fff" strokeWidth={1.7} strokeLinecap="round" />
                <circle cx={x} cy={markerY + 3.1} r={0.95} fill="#fff" />
              </>
            )}
          </g>
        );
      })}
    </g>
  );
}

// The hover preview is sized like a Camera-tab gallery card (h-64 = 256 px
// tall, plus caption/padding) so a ground-truth photo is actually legible from
// the chart. Markers sit at the very top of the plot, so a preview placed
// above them would run off the top of the card - flip it below the marker
// whenever there isn't room above. Pure/exported for testing.
export const PREVIEW_HEIGHT_PX = 288;
export const previewPlacement = (markerTop) => {
  const below = markerTop < PREVIEW_HEIGHT_PX + 8;
  return {
    below,
    top: below ? markerTop + 16 : markerTop - 8,
    className: below ? "" : "-translate-y-full",
  };
};

const formatMarkerTime = (isoTimestamp) =>
  new Date(isoTimestamp).toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

const LineChartCard = ({
  title,
  historicalData,
  lines = [],
  colSpan = "col-span-1",
  syncId = null,
  height = 300,
  yDomain = null,
  zeroLine = false,
  // Off by default: most callers plot one device's own readings, where a
  // gap means that device didn't report (a real outage worth showing as a
  // visible break). Only the Experiment page's merged multi-source series
  // needs this on - there, a gap means "the OTHER source's row," not a
  // missing reading, so bridging it is correct rather than data loss.
  connectNulls = false,
  // Optional vertical markers for real step-boundary transitions (see
  // useExperiment's deriveStepBoundaries) - an array of {x, label}, each
  // its own dashed ReferenceLine. Deliberately a different dash pattern
  // ("2 2") from zeroLine's ("4 3") so the two stay visually distinct when
  // both are present on the same chart (e.g. the gap charts).
  stepMarkers = [],
  // Ground-truth camera markers: an array of {id, x (epoch ms, matching
  // timeNumeric's units), thumbnailUrl, capturedAt (ISO string)}. Hovering
  // one shows its thumbnail in a floating preview positioned over the
  // hovered marker - thumbnailUrl must already be an authenticated blob
  // URL (see useThumbnailCache), never the raw protected API URL, since a
  // plain <img src> can't carry the JWT/session auth these endpoints need.
  imageMarkers = [],
  // Called with the clicked marker (same {id, ...} objects passed in
  // imageMarkers) - the page uses it to open the full-size image dialog.
  onImageMarkerClick = null,
}) => {
  const containerRef = useRef(null);
  const [hoveredMarker, setHoveredMarker] = useState(null);

  const handleMarkerHover = (marker, event) => {
    if (!marker) {
      setHoveredMarker(null);
      return;
    }
    const containerRect = containerRef.current?.getBoundingClientRect();
    const markerRect = event.currentTarget.getBoundingClientRect();
    if (!containerRect) return;
    setHoveredMarker({
      ...marker,
      left: markerRect.left - containerRect.left + markerRect.width / 2,
      top: markerRect.top - containerRect.top,
    });
  };

  const getYAxisIds = () => {
    const lineAxisIds = lines.map((line) => line.yAxisId).filter(Boolean);
    const uniqueAxisIds = [...new Set(lineAxisIds)];
    return uniqueAxisIds.length > 0 ? uniqueAxisIds : [];
  };

  const yAxisIds = getYAxisIds();
  const xTicks = computeXTicks(historicalData);
  const xDomain = getTimeDomain(historicalData);

  return (
    <div ref={containerRef} className={`relative bg-white rounded-xl shadow-sm border p-6 ${colSpan}`}>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={height}>
        {/* syncMethod="value" - real bug found 2026-09-26: Recharts'
            default syncId behavior (syncMethod="index") re-dispatches the
            HOVERED chart's own raw pixel coordinate verbatim to every other
            synced chart, without remapping it to that chart's own plot
            width/offset (see recharts' useChartSynchronisation.js -
            syncMethod === 'index' just forwards the action unchanged).
            That's invisible as long as every synced chart is the same
            width, but this page's Water level chart is
            double-width (col-span-2) - reusing a pixel X measured against a
            narrower sibling chart lands proportionally too far left once
            applied to this chart's own (wider) coordinate space, so the
            cursor line and the tooltip's reported label visibly disagreed
            (label said 16:00, cursor line sat at ~15:44). syncMethod="value"
            makes every chart look up its OWN matching data point by actual
            axis value and compute the coordinate from its own ticks,
            instead of trusting a borrowed pixel position. */}
        <LineChart
          data={historicalData}
          syncId={syncId}
          syncMethod="value"
          margin={{ top: imageMarkers.length > 0 ? MARKER_TOP_MARGIN : 5, right: 5, bottom: 5, left: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="timeNumeric" // Changed from "time"
            stroke="#666"
            fontSize={12}
            angle={-45}
            textAnchor="end"
            height={75}
            domain={["dataMin", "dataMax"]}
            type="number" // Changed from "category"
            scale="time" // Added for proper time scaling
            ticks={xTicks}
            tickFormatter={(timestamp) => {
              // Format the numeric timestamp back to readable format
              return new Date(timestamp).toLocaleString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              });
            }}
          />

          {yAxisIds.length === 0 ? (
            <YAxis stroke="#666" fontSize={12} orientation="left" {...(yDomain ? { domain: yDomain } : {})} />
          ) : (
            yAxisIds.map((axisId, index) => (
              <YAxis
                key={axisId}
                yAxisId={axisId}
                stroke="#666"
                fontSize={12}
                orientation={index % 2 === 0 ? "left" : "right"}
                // Recharts' implicit default floors a numeric axis at 0,
                // which pins a series that never goes near 0 (e.g. PadiHAT's
                // ~33-34°C internal temp) into a sliver at the very top of
                // the chart - auto-scale to the data's own range instead so
                // its real variation actually fills the height.
                domain={["auto", "auto"]}
              />
            ))
          )}

          {connectNulls ? (
            // isAnimationActive={false} - real flicker found live
            // (2026-09-24): Recharts animates the tooltip wrapper's
            // position/visibility on every active-point change (a
            // `transition: transform 400ms` inline style, confirmed by
            // inspecting the wrapper mid-hover), which on a chart with many
            // closely-spaced points means the active point changes faster
            // than that 400ms animation can settle, so the wrapper is very
            // often mid-transition (visibility: hidden) exactly when a
            // screenshot/observation catches it. Disabling the animation
            // makes every position/visibility change instantaneous instead
            // of racing a 400ms transition.
            //
            // filterNull={false} - real bug found 2026-09-26: Recharts
            // computes its OWN "does this row have a value" check to decide
            // whether to show the wrapper at all (hasPayload, in
            // TooltipBoundingBox), independently of whatever our custom
            // `content` renders - by default it hides the wrapper whenever
            // every Line's raw value at the exact hovered row is null. That
            // defeats the entire point of NearestValueTooltip above: this
            // merged multi-source data has only one source's fields defined
            // per row, so "the exact row has no value" is the COMMON case,
            // not an edge case - dense fields (chamber temp/RH) mask it by
            // having a real value on most rows, but the comparatively
            // sparse PadiHAT water-level/battery fields hit it on most
            // hover positions, which read as "this chart's tooltip just
            // never appears." filterNull={false} stops Recharts from
            // gating visibility on its own exact-row payload and leaves
            // the null-handling entirely to NearestValueTooltip, which
            // already does its own real nearest-sample lookup.
            <Tooltip
              content={<NearestValueTooltip data={historicalData} lines={lines} />}
              isAnimationActive={false}
              filterNull={false}
            />
          ) : (
            <Tooltip
              contentStyle={{
                backgroundColor: "white",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
              }}
              labelFormatter={(timestamp) => {
                return new Date(timestamp).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                });
              }}
              formatter={(value, name) => [
                typeof value === "number" ? value.toFixed(2) : value,
                name,
              ]}
              isAnimationActive={false}
            />
          )}

          {zeroLine && <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="4 3" strokeWidth={1} />}

          {stepMarkers.map((marker, index) => (
            <ReferenceLine
              key={`step-marker-${index}-${marker.x}`}
              x={marker.x}
              stroke="#94a3b8"
              strokeDasharray="2 2"
              label={{ value: marker.label, position: "top", fontSize: 10 }}
            />
          ))}

          {/* Only worth the vertical space once there's more than one line to
              tell apart - a single-line chart's title already says what it is. */}
          {lines.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}

          {/* isAnimationActive={false} - real flicker found live (2026-10-21): the stable-props work
              (see the memo note at the bottom) stops redraws for a poll that brought nothing new, but
              during a live run nearly every poll DOES bring a new point, and Recharts replays each
              Line's ~1.5s draw-in animation on any data change, hiding its dots until it ends. On a
              15 s poll that read as the whole chart flashing on every update. */}
          {lines.map((line, index) => (
            <Line
              key={index}
              isAnimationActive={false}
              type="monotone"
              dataKey={line.dataKey}
              stroke={line.color || "#63cdf1ff"}
              strokeWidth={line.strokeWidth || 2}
              name={line.name || line.dataKey}
              dot={shouldShowDots(historicalData, line.dataKey, line.dot)}
              yAxisId={line.yAxisId}
              connectNulls={connectNulls}
            />
          ))}

          {imageMarkers.length > 0 && xDomain && (
            <Customized component={ImageMarkerLayer} markers={imageMarkers} domain={xDomain} onHover={handleMarkerHover} onClick={onImageMarkerClick} />
          )}
        </LineChart>
      </ResponsiveContainer>

      {hoveredMarker?.thumbnailUrl && (() => {
        const placement = previewPlacement(hoveredMarker.top);
        return (
          <div
            className={`pointer-events-none absolute z-10 -translate-x-1/2 ${placement.className} rounded-lg border border-gray-200 bg-white p-1 shadow-lg`}
            style={{ left: hoveredMarker.left, top: placement.top }}
            data-testid="camera-marker-preview"
          >
            <img
              src={hoveredMarker.thumbnailUrl}
              alt="Ground-truth ruler capture"
              className={`h-64 w-auto max-w-[22rem] rounded object-contain ${imageRotationClass(hoveredMarker.rotationDeg)}`}
            />
            {hoveredMarker.capturedAt && (
              <div className="mt-1 text-center text-[10px] text-gray-500">
                {formatMarkerTime(hoveredMarker.capturedAt)}
                {hoveredMarker.labelText ? ` · ${hoveredMarker.labelText}` : ""}
                {onImageMarkerClick ? " · click for full size" : ""}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

// Memoised: the Experiment page re-renders for many unrelated reasons (a status poll, a thumbnail
// arriving, a label saved) and every chart used to be rebuilt each time. With unchanged props a chart is
// now left alone; callers keep their props stable (memoised data/lines, useCallback handlers).
export default memo(LineChartCard);
